import { TodoItem, TodoList } from '../../types/todo';

// ================================
// Date Helper Functions
// ================================

/**
 * Get the start and end dates for a given number of days back from today
 */
export const getDateRange = (days: number, fromDate?: Date): { startDate: Date; endDate: Date } => {
  const baseDate = fromDate || new Date();
  const today = new Date(baseDate.getFullYear(), baseDate.getMonth(), baseDate.getDate());
  const startDate = new Date(today.getTime() - (days - 1) * 24 * 60 * 60 * 1000);
  const endDate = new Date(today.getTime() + 24 * 60 * 60 * 1000);
  return { startDate, endDate };
};

/**
 * Check if a date is today
 */
export const isToday = (date: Date): boolean => {
  const today = new Date();
  return date.toDateString() === today.toDateString();
};

/**
 * Check if a task is overdue (has due date in the past and is not completed)
 */
export const isOverdue = (task: TodoItem): boolean => {
  if (!task.dueDate || task.isCompleted) return false;
  return new Date(task.dueDate) < new Date();
};

/**
 * Check if a task is due today
 */
export const isDueToday = (task: TodoItem): boolean => {
  if (!task.dueDate) return false;
  return isToday(new Date(task.dueDate));
};

/**
 * Get the age of an overdue task in days
 */
export const getOverdueAge = (task: TodoItem): number => {
  if (!task.dueDate || task.isCompleted) return 0;
  const dueDate = new Date(task.dueDate);
  const now = new Date();
  const diffTime = now.getTime() - dueDate.getTime();
  return Math.floor(diffTime / (1000 * 60 * 60 * 24));
};

// ================================
// Task Analysis Functions
// ================================

/**
 * Calculate completion rate for a set of tasks
 */
export const calculateCompletionRate = (tasks: TodoItem[]): number => {
  if (tasks.length === 0) return 0;
  const completed = tasks.filter(t => t.isCompleted).length;
  return Math.round((completed / tasks.length) * 100);
};

/**
 * Get tasks created within a specific date range
 */
export const getTasksInDateRange = (
  tasks: TodoItem[], 
  startDate: Date, 
  endDate: Date
): TodoItem[] => {
  return tasks.filter(t => {
    const createdDate = new Date(t.createdAt);
    return createdDate >= startDate && createdDate < endDate;
  });
};

/**
 * Get tasks completed within a specific date range
 */
export const getCompletedTasksInDateRange = (
  tasks: TodoItem[], 
  startDate: Date, 
  endDate: Date
): TodoItem[] => {
  return tasks.filter(t => {
    if (!t.isCompleted || !t.completedAt) return false;
    const completedDate = new Date(t.completedAt);
    return completedDate >= startDate && completedDate < endDate;
  });
};

/**
 * Calculate productivity score based on multiple factors
 * @param tasks Array of todos
 * @returns Score from 0-100
 */
export const calculateProductivityScore = (tasks: TodoItem[]): number => {
  if (tasks.length === 0) return 0;

  // Factor 1: Completion Rate (40% weight)
  const completionRate = calculateCompletionRate(tasks);
  
  // Factor 2: Deadline Adherence (35% weight)
  const tasksWithDueDates = tasks.filter(t => t.dueDate);
  const overdueTasks = tasksWithDueDates.filter(isOverdue);
  const deadlineAdherence = tasksWithDueDates.length > 0 ? 
    Math.max(0, 100 - ((overdueTasks.length / tasksWithDueDates.length) * 100)) : 100;
  
  // Factor 3: Consistency - tasks completed in last week (25% weight)
  const { startDate: weekStart } = getDateRange(7);
  const recentTasks = getTasksInDateRange(tasks, weekStart, new Date());
  const recentCompletions = recentTasks.filter(t => t.isCompleted);
  const consistency = recentTasks.length > 0 ? 
    Math.min(100, (recentCompletions.length / Math.max(1, recentTasks.length / 7)) * 100) : 0;
  
  // Calculate weighted score
  const score = (completionRate * 0.4) + (deadlineAdherence * 0.35) + (consistency * 0.25);
  return Math.round(Math.max(0, Math.min(100, score)));
};

/**
 * Get urgency level for a task based on due date and priority
 */
export const getUrgencyLevel = (task: TodoItem): 'critical' | 'warning' | 'normal' => {
  if (task.isCompleted) return 'normal';
  
  const overdueAge = getOverdueAge(task);
  const isHighPriority = task.priority === 'High';
  
  // Critical: >7 days overdue OR high priority overdue
  if (overdueAge > 7 || (overdueAge > 0 && isHighPriority)) {
    return 'critical';
  }
  
  // Warning: 1-7 days overdue OR due today with high priority
  if (overdueAge > 0 || (isDueToday(task) && isHighPriority)) {
    return 'warning';
  }
  
  return 'normal';
};

/**
 * Calculate average task completion time in hours
 */
export const calculateAverageCompletionTime = (tasks: TodoItem[]): number => {
  const completedTasks = tasks.filter(t => t.isCompleted && t.completedAt);
  if (completedTasks.length === 0) return 0;
  
  const totalTime = completedTasks.reduce((sum, task) => {
    const created = new Date(task.createdAt).getTime();
    const completed = new Date(task.completedAt!).getTime();
    return sum + (completed - created);
  }, 0);
  
  // Convert from milliseconds to hours
  return totalTime / completedTasks.length / (1000 * 60 * 60);
};

// ================================
// List Analysis Functions
// ================================

/**
 * Calculate health score for a todo list
 */
export const calculateListHealth = (list: TodoList): number => {
  if (!list.items || list.items.length === 0) return 0;
  return calculateCompletionRate(list.items);
};

/**
 * Get the most active lists based on task count and recent activity
 */
export const getMostActiveLists = (
  lists: TodoList[], 
  limit: number = 5
): Array<{ list: TodoList; activityScore: number }> => {
  const { startDate: weekStart } = getDateRange(7);
  
  return lists
    .map(list => {
      const totalTasks = list.items?.length || 0;
      const recentTasks = list.items?.filter(t => 
        new Date(t.createdAt) >= weekStart
      ).length || 0;
      const completedRecently = list.items?.filter(t => 
        t.isCompleted && t.completedAt && new Date(t.completedAt) >= weekStart
      ).length || 0;
      
      // Activity score based on total tasks, recent activity, and completion rate
      const activityScore = (totalTasks * 0.4) + (recentTasks * 0.4) + (completedRecently * 0.2);
      
      return { list, activityScore };
    })
    .filter(item => item.activityScore > 0)
    .sort((a, b) => b.activityScore - a.activityScore)
    .slice(0, limit);
};

// ================================
// Priority Analysis Functions
// ================================

/**
 * Get priority breakdown with completion statistics
 */
export const getPriorityBreakdown = (tasks: TodoItem[]) => {
  const priorities = ['High', 'Medium', 'Low'] as const;
  
  return priorities.reduce((breakdown, priority) => {
    const priorityTasks = tasks.filter(t => t.priority === priority);
    const completed = priorityTasks.filter(t => t.isCompleted).length;
    const total = priorityTasks.length;
    
    breakdown[priority.toLowerCase() as keyof typeof breakdown] = {
      total,
      completed,
      pending: total - completed,
      completionRate: total > 0 ? Math.round((completed / total) * 100) : 0,
      overdue: priorityTasks.filter(isOverdue).length
    };
    
    return breakdown;
  }, {} as Record<'high' | 'medium' | 'low', {
    total: number;
    completed: number;
    pending: number;
    completionRate: number;
    overdue: number;
  }>);
};

// ================================
// Time-based Analysis Functions
// ================================

/**
 * Get completion pattern by hour of day
 */
export const getCompletionPatternByHour = (tasks: TodoItem[]): number[] => {
  const hourCounts = Array.from({ length: 24 }, () => 0);
  
  tasks
    .filter(t => t.isCompleted && t.completedAt)
    .forEach(t => {
      const hour = new Date(t.completedAt!).getHours();
      hourCounts[hour]++;
    });
  
  return hourCounts;
};

/**
 * Get most productive time of day
 */
export const getMostProductiveTimeOfDay = (tasks: TodoItem[]): string => {
  const hourCounts = getCompletionPatternByHour(tasks);
  const maxHour = hourCounts.indexOf(Math.max(...hourCounts));
  
  if (maxHour >= 6 && maxHour < 12) return 'Morning';
  if (maxHour >= 12 && maxHour < 18) return 'Afternoon';
  if (maxHour >= 18 && maxHour < 22) return 'Evening';
  return 'Night';
};

/**
 * Calculate completion streak (consecutive days with completions)
 */
export const calculateCompletionStreak = (tasks: TodoItem[]): number => {
  const completedTasks = tasks
    .filter(t => t.isCompleted && t.completedAt)
    .sort((a, b) => new Date(b.completedAt!).getTime() - new Date(a.completedAt!).getTime());
  
  if (completedTasks.length === 0) return 0;
  
  let streak = 0;
  const today = new Date();
  
  // Check each day going backwards from today
  for (let i = 0; i < 30; i++) { // Check up to 30 days
    const checkDate = new Date(today.getTime() - i * 24 * 60 * 60 * 1000);
    const nextDay = new Date(checkDate.getTime() + 24 * 60 * 60 * 1000);
    
    const hasCompletionOnDay = completedTasks.some(t => {
      const completedDate = new Date(t.completedAt!);
      return completedDate >= checkDate && completedDate < nextDay;
    });
    
    if (hasCompletionOnDay) {
      streak++;
    } else if (i > 0) { // Don't break on first day (today) if no completions
      break;
    }
  }
  
  return streak;
};

// ================================
// Trend Analysis Functions
// ================================

/**
 * Calculate week-over-week change percentage
 */
export const calculateWeekOverWeekChange = (
  tasks: TodoItem[], 
  metric: 'completions' | 'creations' = 'completions'
): { current: number; previous: number; changePercent: number } => {
  const { startDate: thisWeekStart } = getDateRange(7);
  const { startDate: lastWeekStart, endDate: lastWeekEnd } = getDateRange(7, 
    new Date(thisWeekStart.getTime() - 7 * 24 * 60 * 60 * 1000)
  );
  
  let thisWeekCount: number;
  let lastWeekCount: number;
  
  if (metric === 'completions') {
    thisWeekCount = getCompletedTasksInDateRange(tasks, thisWeekStart, new Date()).length;
    lastWeekCount = getCompletedTasksInDateRange(tasks, lastWeekStart, lastWeekEnd).length;
  } else {
    thisWeekCount = getTasksInDateRange(tasks, thisWeekStart, new Date()).length;
    lastWeekCount = getTasksInDateRange(tasks, lastWeekStart, lastWeekEnd).length;
  }
  
  const changePercent = lastWeekCount > 0 ? 
    Math.round(((thisWeekCount - lastWeekCount) / lastWeekCount) * 100) : 
    (thisWeekCount > 0 ? 100 : 0);
  
  return {
    current: thisWeekCount,
    previous: lastWeekCount,
    changePercent
  };
};

/**
 * Get productivity trend direction
 */
export const getProductivityTrend = (
  weeklyCompletions: number[]
): 'improving' | 'stable' | 'declining' => {
  if (weeklyCompletions.length < 4) return 'stable';
  
  const recent = weeklyCompletions.slice(-3);
  const earlier = weeklyCompletions.slice(0, -3);
  
  const recentAvg = recent.reduce((a, b) => a + b, 0) / recent.length;
  const earlierAvg = earlier.reduce((a, b) => a + b, 0) / earlier.length;
  
  if (recentAvg > earlierAvg * 1.1) return 'improving';
  if (recentAvg < earlierAvg * 0.9) return 'declining';
  return 'stable';
};

// ================================
// Goal Tracking Functions
// ================================

/**
 * Calculate progress towards a daily goal
 */
export const calculateDailyGoalProgress = (
  tasks: TodoItem[], 
  dailyGoal: number
): { completed: number; progress: number; isAchieved: boolean } => {
  const today = new Date();
  const todayStart = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  const todayEnd = new Date(todayStart.getTime() + 24 * 60 * 60 * 1000);
  
  const completedToday = tasks.filter(t => 
    t.isCompleted && t.completedAt && 
    new Date(t.completedAt) >= todayStart && 
    new Date(t.completedAt) < todayEnd
  ).length;
  
  return {
    completed: completedToday,
    progress: Math.min(100, Math.round((completedToday / dailyGoal) * 100)),
    isAchieved: completedToday >= dailyGoal
  };
};

/**
 * Calculate progress towards a weekly goal
 */
export const calculateWeeklyGoalProgress = (
  tasks: TodoItem[], 
  weeklyGoal: number
): { completed: number; progress: number; isAchieved: boolean } => {
  const { startDate: weekStart } = getDateRange(7);
  const completedThisWeek = getCompletedTasksInDateRange(tasks, weekStart, new Date()).length;
  
  return {
    completed: completedThisWeek,
    progress: Math.min(100, Math.round((completedThisWeek / weeklyGoal) * 100)),
    isAchieved: completedThisWeek >= weeklyGoal
  };
};

// ================================
// Export all utilities
// ================================

export const DashboardAnalytics = {
  // Date helpers
  getDateRange,
  isToday,
  isOverdue,
  isDueToday,
  getOverdueAge,
  
  // Task analysis
  calculateCompletionRate,
  getTasksInDateRange,
  getCompletedTasksInDateRange,
  calculateProductivityScore,
  getUrgencyLevel,
  calculateAverageCompletionTime,
  
  // List analysis
  calculateListHealth,
  getMostActiveLists,
  
  // Priority analysis
  getPriorityBreakdown,
  
  // Time-based analysis
  getCompletionPatternByHour,
  getMostProductiveTimeOfDay,
  calculateCompletionStreak,
  
  // Trend analysis
  calculateWeekOverWeekChange,
  getProductivityTrend,
  
  // Goal tracking
  calculateDailyGoalProgress,
  calculateWeeklyGoalProgress
};